import { useMemo, useState } from "react";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { Bug, LoaderCircle, TriangleAlert } from "lucide-react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { auth, db } from "../lib/firebase";
import { captureSentryMessage } from "../lib/sentry";
import { useAppStore } from "../store/useAppStore";
import { cn } from "../lib/utils";
import type { SupportReportCategory, SupportReportSeverity } from "../types";
import { SUPPORT_EMAIL_HONEYPOT_FIELD, SUPPORT_EMAIL_MIN_AGE_MS, sendSupportEmail } from "../lib/supportEmail";

const REPORT_CATEGORIES: SupportReportCategory[] = ["Bug", "Data issue", "Billing", "Access", "Other"];
const REPORT_SEVERITIES: SupportReportSeverity[] = ["Low", "Medium", "High", "Urgent"];

type ReportProblemButtonProps = {
  buttonLabel?: string;
  className?: string;
  triggerVariant?: "default" | "outline" | "secondary" | "ghost" | "destructive" | "link";
  triggerSize?: "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg";
};

export function ReportProblemButton({
  buttonLabel = "Report a problem",
  className,
  triggerVariant = "outline",
  triggerSize = "default",
}: ReportProblemButtonProps) {
  const location = useLocation();
  const { businessName, planId, activeAiProvider } = useAppStore();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState<SupportReportCategory>("Bug");
  const [severity, setSeverity] = useState<SupportReportSeverity>("Medium");
  const [website, setWebsite] = useState("");
  const [openedAt, setOpenedAt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pagePath = useMemo(() => `${location.pathname}${location.search}${location.hash}`, [location.hash, location.pathname, location.search]);
  const pageUrl = typeof window !== "undefined" ? window.location.href : pagePath;
  const appVersion = import.meta.env.VITE_APP_VERSION?.trim() || "local";

  const resetForm = () => {
    setSummary("");
    setDetails("");
    setCategory("Bug");
    setSeverity("Medium");
    setWebsite("");
    setOpenedAt("");
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setOpenedAt(new Date().toISOString());
      setWebsite("");
    } else {
      resetForm();
    }
  };

  const handleSubmit = async () => {
    const trimmedSummary = summary.trim();
    const trimmedDetails = details.trim();

    if (!trimmedSummary || !trimmedDetails) {
      toast.error("Please add both a short summary and a few details.");
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error("You need to be signed in to send a report.");
      return;
    }

    const openedTimestamp = openedAt ? Date.parse(openedAt) : Number.NaN;
    if (!Number.isFinite(openedTimestamp) || Date.now() - openedTimestamp < SUPPORT_EMAIL_MIN_AGE_MS) {
      toast.error("Please take a moment to describe the issue before sending.");
      return;
    }

    setIsSubmitting(true);
    const reportId = uuidv4();
    const createdAt = new Date().toISOString();
    const reportRef = doc(db, "supportReports", reportId);
    const sentryEventId = captureSentryMessage(`User report: ${trimmedSummary}`, {
      level: "error",
      tags: {
        report_category: category,
        report_severity: severity,
        report_page: location.pathname,
        report_plan: planId,
      },
      extra: {
        reportId,
        summary: trimmedSummary,
        details: trimmedDetails,
        pagePath,
        pageUrl,
        businessName,
        activeAiProvider,
        appVersion,
      },
    });

    const supportEmailPayload = {
      reportId,
      summary: trimmedSummary,
      details: trimmedDetails,
      category,
      severity,
      pagePath,
      pageUrl,
      businessName,
      planId,
      activeAiProvider,
      appVersion,
      sentryEventId: sentryEventId || "",
      openedAt,
      [SUPPORT_EMAIL_HONEYPOT_FIELD]: website,
    };

    try {
      const [saveOutcome, emailOutcome] = await Promise.allSettled([
        setDoc(reportRef, {
          id: reportId,
          userId: currentUser.uid,
          userEmail: currentUser.email || "",
          userName: currentUser.displayName || "",
          businessName,
          planId,
          activeAiProvider,
          category,
          severity,
          summary: trimmedSummary,
          details: trimmedDetails,
          pagePath,
          pageUrl,
          appVersion,
          sentryEventId: sentryEventId || "",
          emailNotificationStatus: "pending",
          status: "open",
          createdAt,
        }),
        currentUser.getIdToken().then((idToken) => sendSupportEmail(supportEmailPayload, idToken)),
      ]);

      if (saveOutcome.status === "fulfilled") {
        if (emailOutcome.status === "fulfilled") {
          await updateDoc(reportRef, {
            emailNotificationStatus: "sent",
            emailNotificationMessageId: emailOutcome.value.messageId || "",
            emailNotificationSentAt: new Date().toISOString(),
            emailNotificationError: "",
          });
          toast.success("Thanks — your issue was emailed to Stephen and saved for review.");
        } else {
          await updateDoc(reportRef, {
            emailNotificationStatus: "failed",
            emailNotificationError: emailOutcome.reason?.message || "Email delivery failed",
          }).catch(() => undefined);
          toast.success("Thanks — your issue was saved for review, but the email notification failed.");
        }
      } else if (emailOutcome.status === "fulfilled") {
        toast.success("Thanks — your issue was emailed to Stephen, but the report copy could not be saved.");
      } else {
        throw saveOutcome.reason || emailOutcome.reason || new Error("Could not send your report right now.");
      }

      resetForm();
      setOpen(false);
    } catch (error: any) {
      toast.error(error?.message || "Could not send your report right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        variant={triggerVariant}
        size={triggerSize}
        className={cn(
          "gap-2",
          className,
        )}
        onClick={() => setOpen(true)}
      >
        <Bug className="h-4 w-4" />
        {buttonLabel}
      </Button>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>
            Tell us what went wrong. We’ll email the report to Stephen’s inbox and save a copy for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Include the steps you took and what you expected to happen. The more specific, the better.</p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="problem-summary">Short summary</Label>
            <Input
              id="problem-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Example: Lead import failed after upload"
              autoComplete="off"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="problem-details">What happened?</Label>
            <Textarea
              id="problem-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Tell us what you were trying to do, what you saw, and anything odd in the UI or console."
              className="min-h-32"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="problem-category">Category</Label>
              <Select value={category} onValueChange={(value) => setCategory(value as SupportReportCategory)}>
                <SelectTrigger id="problem-category">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_CATEGORIES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="problem-severity">Severity</Label>
              <Select value={severity} onValueChange={(value) => setSeverity(value as SupportReportSeverity)}>
                <SelectTrigger id="problem-severity">
                  <SelectValue placeholder="How urgent is it?" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_SEVERITIES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="problem-page">Current page</Label>
            <Input id="problem-page" value={pagePath} readOnly />
          </div>

          <div className="sr-only" aria-hidden="true">
            <Label htmlFor="problem-website">Website</Label>
            <Input
              id="problem-website"
              name={SUPPORT_EMAIL_HONEYPOT_FIELD}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter className="items-center">
          <div className="flex flex-1 flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>App version: {appVersion}</span>
            <span>•</span>
            <span>Plan: {planId}</span>
          </div>
          <DialogClose render={<Button variant="outline" disabled={isSubmitting} />}>Cancel</DialogClose>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              "Send report"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
